import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useToast } from '../../../contexts/toast-context';

interface LiveWaveformProps {
  barWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  speed?: number;
  barColor?: string;
  backgroundColor?: string;
  barGap?: number;
  amplitudeBoost?: number;
  paused?: boolean;
  resetTrigger?: number;
  recordingActive?: boolean;
}

interface Bar {
  x: number;
  height: number;
}

export const LiveWaveform: React.FC<LiveWaveformProps> = ({
  barWidth = 3,
  barGap = 4,
  minHeight = 4,
  maxHeight = 45,
  speed = 2,
  barColor = '#5E84ED',
  backgroundColor = '#23252a',

  amplitudeBoost = 0.8,
  paused = false,
  resetTrigger = 0,
  recordingActive = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const barsRef = useRef<Bar[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const { showError } = useToast();

  const smoothingFactor = 0.8;
  const smoothedAmplitudeRef = useRef(0);

  const initializeMicrophone = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();

      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.7;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      streamRef.current = stream;
      setIsInitialized(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      showError('Microphone access denied or unavailable');
      setIsInitialized(false);
    }
  }, [showError]);

  const getAmplitude = useCallback(() => {
    if (!analyserRef.current) return 0;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Focus on lower frequencies (voice range) and calculate peak amplitude
    let maxAmplitude = 0;
    const voiceRangeEnd = Math.floor(bufferLength * 0.3); // First 30% of frequencies

    for (let i = 0; i < voiceRangeEnd; i++) {
      maxAmplitude = Math.max(maxAmplitude, dataArray[i]);
    }

    let normalizedAmplitude = maxAmplitude / 255;

    // Apply exponential boost for better responsiveness to normal speech
    normalizedAmplitude = Math.pow(normalizedAmplitude, 1) * amplitudeBoost;

    // Dynamic range compression to handle loud sounds without clipping
    if (normalizedAmplitude > 0.8) {
      normalizedAmplitude = 0.8 + (normalizedAmplitude - 0.8) * 0.3;
    }

    // Ensure we don't exceed 1.0
    normalizedAmplitude = Math.min(1, normalizedAmplitude);

    // Apply smoothing
    smoothedAmplitudeRef.current =
      smoothedAmplitudeRef.current * smoothingFactor + normalizedAmplitude * (1 - smoothingFactor);

    return smoothedAmplitudeRef.current;
  }, [amplitudeBoost, smoothingFactor]);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isInitialized || paused) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    barsRef.current = barsRef.current
      .map((b) => ({ ...b, x: b.x - speed }))
      .filter((b) => b.x + barWidth > 0);

    const amplitude = getAmplitude();
    const barHeight = minHeight + amplitude * (maxHeight - minHeight);

    const last = barsRef.current[barsRef.current.length - 1];
    if (!last || last.x <= canvas.width - (barWidth + barGap)) {
      barsRef.current.push({ x: canvas.width, height: barHeight });
    }

    ctx.fillStyle = barColor;
    const mid = canvas.height / 2;

    barsRef.current.forEach((bar) => {
      ctx.fillRect(bar.x, mid - bar.height / 2, barWidth, bar.height);
    });

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [
    barWidth,
    barGap,
    minHeight,
    maxHeight,
    speed,
    backgroundColor,
    barColor,
    getAmplitude,
    isInitialized,
    paused,
  ]);

  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    audioContextRef.current?.close();
  }, []);

  useEffect(() => {
    // Only manage microphone lifecycle based on recordingActive prop
    // Don't initialize microphone on mount - wait for recordingActive to be true
    return cleanup;
  }, []);

  useEffect(() => {
    if (isInitialized && !paused) {
      animate();
    } else if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, [isInitialized, paused]);

  useEffect(() => {
    // Reset bars and smoothed amplitude when resetTrigger changes
    barsRef.current = [];
    smoothedAmplitudeRef.current = 0;
  }, [resetTrigger]);

  useEffect(() => {
    if (recordingActive && !isInitialized) {
      initializeMicrophone();
    } else if (!recordingActive && isInitialized) {
      // Stop microphone when recording is not active
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      setIsInitialized(false);
    }
  }, [recordingActive, isInitialized]);

  return (
    <canvas
      ref={canvasRef}
      height={45}
      width={200}
      style={{
        width: '100%',
        backgroundColor,
        borderRadius: '4px',
        display: 'block',
      }}
    />
  );
};
